import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Slide,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BackpackIcon from '@mui/icons-material/Backpack';
import StyleIcon from '@mui/icons-material/Style';
import DescriptionIcon from '@mui/icons-material/Description';
import PlaceIcon from '@mui/icons-material/Place';
import StarsIcon from '@mui/icons-material/Stars';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAuth } from 'firebase/auth';
import { useTheme } from '@mui/material/styles';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import cardService from '../services/cardService';
import CardDisplay from './CardDisplay';

const getDb = () => firebase.firestore();

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function PocketDialog({ open, onClose, refreshTrigger = 0 }) {
  const [currentView, setCurrentView] = useState('main'); // main, collectibleSets, setDetail, myNotes, myWaypoints, tieredNotes
  const [selectedSet, setSelectedSet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardsData, setCardsData] = useState([]);
  const [showAllCards, setShowAllCards] = useState(false); // Debug toggle to show all cards
  const [myNotesData, setMyNotesData] = useState({
    holding: [],
    active: [],
    expired: [],
  });
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const theme = useTheme();

  useEffect(() => {
    if (open) {
      setCurrentView('main');
      setSelectedSet(null);
    }
  }, [open]);

  useEffect(() => {
    if (currentView === 'setDetail' && selectedSet && currentUserId) {
      loadSetCards();
    } else if (currentView === 'myNotes' && currentUserId) {
      loadMyNotes();
    }
  }, [currentView, selectedSet, currentUserId, refreshTrigger]);

  const loadSetCards = async () => {
    setLoading(true);
    try {
      const cards = await cardService.getUserSetCollection(currentUserId, selectedSet);
      setCardsData(cards);
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyNotes = async () => {
    setLoading(true);
    try {
      const db = getDb();
      const now = new Date();

      // Notes I'm holding (picked up by me, not collectibles/tiered)
      const holdingSnapshot = await db
        .collection('tokens')
        .where('currentHolder', '==', currentUserId)
        .where('noteType', '==', 'static')
        .get();

      const holdingNotes = [];
      holdingSnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include if I picked it up (not created by me)
        if (data.pickedUpBy === currentUserId && data.createdBy !== currentUserId) {
          holdingNotes.push({ id: doc.id, ...data });
        }
      });

      // Notes I've sent that are active
      const activeSnapshot = await db
        .collection('tokens')
        .where('createdBy', '==', currentUserId)
        .where('noteType', '==', 'static')
        .where('active', '==', true)
        .get();

      const activeNotes = [];
      activeSnapshot.forEach((doc) => {
        const data = doc.data();
        const expiryDate = data.expiresAt?.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);
        if (expiryDate > now) {
          activeNotes.push({ id: doc.id, ...data });
        }
      });

      // Notes I've sent that have expired
      const expiredSnapshot = await db
        .collection('tokens')
        .where('createdBy', '==', currentUserId)
        .where('noteType', '==', 'static')
        .get();

      const expiredNotes = [];
      expiredSnapshot.forEach((doc) => {
        const data = doc.data();
        const expiryDate = data.expiresAt?.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);
        if (expiryDate <= now || data.active === false) {
          expiredNotes.push({ id: doc.id, ...data });
        }
      });

      setMyNotesData({
        holding: holdingNotes,
        active: activeNotes,
        expired: expiredNotes,
      });
    } catch (error) {
      console.error('Error loading my notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentView === 'setDetail') {
      setCurrentView('collectibleSets');
      setSelectedSet(null);
    } else {
      setCurrentView('main');
    }
  };

  const getTitle = () => {
    switch (currentView) {
      case 'collectibleSets':
        return 'Collectible Sets';
      case 'setDetail':
        return `Set ${selectedSet?.replace('set', '')} - Starter Set`;
      case 'myNotes':
        return 'My Notes';
      case 'myWaypoints':
        return 'My Waypoints';
      case 'tieredNotes':
        return 'Tiered Notes';
      default:
        return 'My Pocket';
    }
  };

  // Main menu tiles
  const renderMainMenu = () => {
    const tiles = [
      {
        id: 'collectibleSets',
        title: 'Collectible Sets',
        icon: <StyleIcon sx={{ fontSize: 48 }} />,
        description: 'View your card collection',
        color: '#ff8c00',
      },
      {
        id: 'myNotes',
        title: 'My Notes',
        icon: <DescriptionIcon sx={{ fontSize: 48 }} />,
        description: 'Notes you sent or picked up',
        color: '#4caf50',
      },
      {
        id: 'myWaypoints',
        title: 'My Waypoints',
        icon: <PlaceIcon sx={{ fontSize: 48 }} />,
        description: 'Your saved locations',
        color: '#2196f3',
      },
      {
        id: 'tieredNotes',
        title: 'Tiered Notes',
        icon: <StarsIcon sx={{ fontSize: 48 }} />,
        description: 'Bronze, Silver, Gold, Platinum',
        color: '#9c27b0',
      },
    ];

    return (
      <Grid container spacing={3} sx={{ p: 2 }}>
        {tiles.map((tile) => (
          <Grid item xs={6} sm={6} md={3} key={tile.id}>
            <Card
              onClick={() => setCurrentView(tile.id)}
              sx={{
                cursor: 'pointer',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 8px 24px ${tile.color}40`,
                },
                border: `2px solid ${tile.color}40`,
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 180,
                  textAlign: 'center',
                }}
              >
                <Box sx={{ color: tile.color, mb: 2 }}>
                  {tile.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Gochi Hand, cursive',
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  {tile.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {tile.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Collectible Sets view
  const renderCollectibleSets = () => {
    const sets = [
      {
        id: 'set1',
        name: 'Set 1 - Starter Set',
        description: 'The first collection of 50 cards',
        totalCards: 50,
      },
    ];

    return (
      <Grid container spacing={3} sx={{ p: 2 }}>
        {sets.map((set) => (
          <Grid item xs={12} sm={6} md={4} key={set.id}>
            <Card
              onClick={() => {
                setSelectedSet(set.id);
                setCurrentView('setDetail');
              }}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(255, 140, 0, 0.3)',
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'Gochi Hand, cursive',
                    mb: 1,
                  }}
                >
                  {set.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {set.description}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {set.totalCards} cards
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Set Detail view with cards
  const renderSetDetail = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    const collectedCount = cardsData.filter(card => card.collected).length;
    const progressPercentage = (collectedCount / cardsData.length) * 100;

    return (
      <Box sx={{ p: 2 }}>
        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Gochi Hand, cursive' }}>
              Collection Progress
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'Gochi Hand, cursive' }}>
              {collectedCount} / {cardsData.length}
            </Typography>
          </Box>
          <Box
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progressPercentage}%`,
                background: 'linear-gradient(90deg, #ff8c00, #ffd700)',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              mt: 1,
              color: '#666',
              fontFamily: 'Gochi Hand, cursive',
            }}
          >
            {progressPercentage.toFixed(1)}% Complete
          </Typography>
        </Box>

        {/* Debug Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button
            variant={showAllCards ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setShowAllCards(!showAllCards)}
            sx={{
              fontFamily: 'Gochi Hand, cursive',
              textTransform: 'none',
            }}
          >
            {showAllCards ? '👁️ Showing All Cards' : '👁️ Show All Cards'}
          </Button>
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={0.5} sx={{ justifyContent: 'center' }}>
          {cardsData.map((card) => (
            <Grid item xs={6} sm={3} md={2.4} lg={2} xl={1.5} key={card.cardId}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <CardDisplay
                  card={card}
                  collected={showAllCards || card.collected}
                  count={showAllCards ? 1 : card.count}
                  size="large"
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // My Notes view
  const renderMyNotes = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    const renderNoteSection = (title, notes, color) => (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Gochi Hand, cursive', flex: 1 }}
            >
              {title}
            </Typography>
            <Chip
              label={notes.length}
              sx={{
                backgroundColor: color,
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {notes.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              No notes in this section
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {notes.map((note) => {
                const expiryDate = note.expiresAt?.toDate
                  ? note.expiresAt.toDate()
                  : new Date(note.expiresAt);
                const hoursLeft = Math.max(0, Math.floor((expiryDate - new Date()) / (1000 * 60 * 60)));

                return (
                  <Grid item xs={12} sm={6} md={4} key={note.id}>
                    <Card
                      sx={{
                        backgroundImage: `
                          linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)),
                          repeating-linear-gradient(
                            0deg,
                            transparent,
                            transparent 1px,
                            rgba(200, 190, 170, 0.1) 1px,
                            rgba(200, 190, 170, 0.1) 2px
                          )
                        `,
                        backgroundColor: '#f9f7f4',
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: 'Gochi Hand, cursive',
                            fontSize: '1rem',
                            lineHeight: 1.6,
                            color: '#2c2c2c',
                            whiteSpace: 'pre-wrap',
                            mb: 2,
                          }}
                        >
                          {note.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'Gochi Hand, cursive',
                              color: hoursLeft > 0 ? '#4caf50' : '#f44336',
                            }}
                          >
                            {hoursLeft > 0 ? `${hoursLeft}h left` : 'Expired'}
                          </Typography>
                          {note.pickedUpBy && (
                            <Chip
                              label="Picked up"
                              size="small"
                              sx={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    );

    return (
      <Box sx={{ p: 2 }}>
        {renderNoteSection('Notes I\'m Holding', myNotesData.holding, '#2196f3')}
        {renderNoteSection('My Active Notes', myNotesData.active, '#4caf50')}
        {renderNoteSection('My Expired Notes', myNotesData.expired, '#f44336')}
      </Box>
    );
  };

  // Placeholder views
  const renderPlaceholder = (title) => (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{ fontFamily: 'Gochi Hand, cursive' }}
      >
        {title} - Coming Soon!
      </Typography>
    </Box>
  );

  const renderCurrentView = () => {
    switch (currentView) {
      case 'main':
        return renderMainMenu();
      case 'collectibleSets':
        return renderCollectibleSets();
      case 'setDetail':
        return renderSetDetail();
      case 'myNotes':
        return renderMyNotes();
      case 'myWaypoints':
        return renderPlaceholder('My Waypoints');
      case 'tieredNotes':
        return renderPlaceholder('Tiered Notes');
      default:
        return renderMainMenu();
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      sx={{ backgroundColor: theme.palette.background.default }}
    >
      <AppBar
        position="static"
        sx={{ backgroundColor: theme.palette.primary.main }}
      >
        <Toolbar>
          {currentView !== 'main' && (
            <IconButton edge="start" color="inherit" onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          )}
          {currentView === 'main' && (
            <IconButton edge="start" color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
          <BackpackIcon sx={{ ml: 1, mr: 1 }} />
          <Typography variant="h6" sx={{ fontFamily: 'Gochi Hand, cursive' }}>
            {getTitle()}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ backgroundColor: theme.palette.background.default, p: 0 }}>
        {renderCurrentView()}
      </DialogContent>
    </Dialog>
  );
}

export default PocketDialog;
